import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface CommissionSlab {
  id: string;
  tenant_id: string;
  transaction_type: 'withdraw' | 'deposit';
  amount_from: number;
  amount_to: number;
  commission_amount: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Default slabs for new tenants
const DEFAULT_SLABS = [
  { transaction_type: 'withdraw', amount_from: 100, amount_to: 1000, commission_amount: 1 },
  { transaction_type: 'withdraw', amount_from: 1001, amount_to: 2000, commission_amount: 2 },
  { transaction_type: 'withdraw', amount_from: 2001, amount_to: 5000, commission_amount: 5 },
  { transaction_type: 'withdraw', amount_from: 5001, amount_to: 10000, commission_amount: 7 },
  { transaction_type: 'withdraw', amount_from: 10001, amount_to: 25000, commission_amount: 10 },
  { transaction_type: 'deposit', amount_from: 100, amount_to: 1000, commission_amount: 1 },
  { transaction_type: 'deposit', amount_from: 1001, amount_to: 5000, commission_amount: 2 },
  { transaction_type: 'deposit', amount_from: 5001, amount_to: 25000, commission_amount: 5 },
];

export function useKioskCommissionSlabs() {
  const { tenant } = useAuth();
  const queryClient = useQueryClient();

  const { data: slabs, isLoading, error } = useQuery({
    queryKey: ['kiosk_commission_slabs', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];

      const { data, error } = await supabase
        .from('kiosk_commission_slabs')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('is_active', true)
        .order('transaction_type')
        .order('amount_from');

      if (error) throw error;

      // If no slabs exist, create defaults
      if (!data || data.length === 0) {
        const defaultSlabsWithTenant = DEFAULT_SLABS.map(slab => ({
          ...slab,
          tenant_id: tenant.id,
        }));

        const { data: newSlabs, error: insertError } = await supabase
          .from('kiosk_commission_slabs')
          .insert(defaultSlabsWithTenant)
          .select();

        if (insertError) throw insertError;
        return (newSlabs || []) as CommissionSlab[];
      }

      return data as CommissionSlab[];
    },
    enabled: !!tenant?.id,
  });

  const addSlab = useMutation({
    mutationFn: async (slab: Omit<CommissionSlab, 'id' | 'created_at' | 'updated_at' | 'tenant_id'>) => {
      if (!tenant?.id) throw new Error('No tenant');

      const { error } = await supabase
        .from('kiosk_commission_slabs')
        .insert({ ...slab, tenant_id: tenant.id });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosk_commission_slabs', tenant?.id] });
    },
  });

  const updateSlab = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CommissionSlab> & { id: string }) => {
      const { error } = await supabase
        .from('kiosk_commission_slabs')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosk_commission_slabs', tenant?.id] });
    },
  });

  const deleteSlab = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('kiosk_commission_slabs')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kiosk_commission_slabs', tenant?.id] });
    },
  });

  // Calculate portal commission based on amount and transaction type
  const calculatePortalCommission = (transactionType: string, amount: number): number => {
    if (transactionType !== 'withdraw' && transactionType !== 'deposit') {
      return 0; // No portal commission for balance_enquiry or mini_statement
    }

    if (!slabs || slabs.length === 0 || amount <= 0) return 0;

    const applicableSlabs = slabs.filter(
      slab => slab.transaction_type === transactionType && slab.is_active
    );

    // Find the slab that matches the amount
    const matchingSlab = applicableSlabs.find(
      slab => amount >= slab.amount_from && amount <= slab.amount_to
    );

    return matchingSlab?.commission_amount || 0;
  };

  // Get slabs by transaction type
  const getSlabsByType = (transactionType: 'withdraw' | 'deposit') => {
    return slabs?.filter(slab => slab.transaction_type === transactionType) || [];
  };

  return {
    slabs,
    isLoading,
    error,
    addSlab,
    updateSlab,
    deleteSlab,
    calculatePortalCommission,
    getSlabsByType,
  };
}
